import React, { useCallback, useEffect, useState } from "react";
import { Plug, Copy, Check, ShieldCheck, Link2, Unplug, RefreshCw } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import ConfirmAction from "@/components/ConfirmAction";
import McpKeys from "@/components/McpKeys";
import McpApprovals from "@/components/McpApprovals";
import ShopAiKeys from "@/components/ShopAiKeys";
import LowStockCard from "@/components/LowStockCard";
import { Card, Button, Chip, Field, Input, Skeleton, Toast } from "@/ui";
import { manilaDateTime } from "@/lib/format";

// Shop-side BloomConnect connection. BloomConnect owns messaging + identity; here
// a shop owner simply links (or creates) their messaging account. No secrets are
// stored in the browser.
export default function Connections() {
  const { shopId, shopName } = useAuth();
  const navigate = useNavigate();
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [subInput, setSubInput] = useState("");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // "connect-link" | "connect-new" | "disconnect"

  const load = useCallback(() => {
    if (!shopId) return;
    setLoading(true);
    api.get(`/conversation/shops/${shopId}`)
      .then((r) => { setConn(r.data); setSubInput(r.data.subAccountId || ""); })
      .catch((e) => setError(friendlyError(e)))
      .finally(() => setLoading(false));
  }, [shopId]);

  useEffect(() => {
    if (!shopId) { navigate("/pick-shop", { replace: true }); return; }
    load();
  }, [shopId, load, navigate]);

  const runConnect = async (createNew) => {
    setBusy(true); setError("");
    try {
      const body = createNew ? { createNew: true } : { subAccountId: subInput.trim() };
      const r = await api.post(`/conversation/shops/${shopId}/connect`, body);
      setConn((c) => ({ ...c, ...r.data }));
      setToast("BloomConnect is now connected to your shop");
    } catch (e) { setError(friendlyError(e)); }
    finally { setBusy(false); setConfirm(null); setTimeout(() => setToast(""), 3500); }
  };

  const runDisconnect = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post(`/conversation/shops/${shopId}/disconnect`, {});
      setConn((c) => ({ ...c, ...r.data }));
      setToast("BloomConnect has been switched off for your shop");
    } catch (e) { setError(friendlyError(e)); }
    finally { setBusy(false); setConfirm(null); setTimeout(() => setToast(""), 3500); }
  };

  const verifyTools = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post(`/conversation/shops/${shopId}/verify-tools`, {});
      setToast(r.data.ok ? "Your shop's tools checked out fine" : (r.data.message || "Could not check tools"));
      load();
    } catch (e) { setError(friendlyError(e)); }
    finally { setBusy(false); setTimeout(() => setToast(""), 3500); }
  };

  const copyMcp = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(conn.mcpConfig, null, 2));
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    } catch { setToast("Copy did not work — select the text and copy it by hand"); setTimeout(() => setToast(""), 3000); }
  };

  return (
    <Layout shopName={shopName}>
      <div className="row-between" style={{ marginBottom: 16 }}>
        <h1 className="t-h1">connections</h1>
        {conn ? (
          <Chip tone={conn.connected ? "good" : "warn"} dot data-testid="connection-status">
            {conn.connected ? "connected" : "not connected"}
          </Chip>
        ) : null}
      </div>
      <p className="muted" style={{ marginBottom: 20, maxWidth: 640 }}>
        BloomConnect handles all your messaging — chats, the assistant and your customer list live there.
        Link your shop's BloomConnect account so orders and points flow back to your conversations.
      </p>

      {error ? <div className="err-text" data-testid="connections-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      {loading || !conn ? (
        <div className="stack"><Skeleton variant="card" /><Skeleton variant="card" /></div>
      ) : (
        <div className="grid grid-2" style={{ alignItems: "start" }}>
          {/* Status + actions */}
          <Card data-testid="connection-card">
            <div className="row" style={{ gap: 10, marginBottom: 12 }}>
              <span className="ph-icon" style={{ margin: 0 }}><Plug size={18} strokeWidth={1.75} /></span>
              <h2 className="t-h2">BloomConnect</h2>
            </div>

            <div className="stack" style={{ gap: 10 }}>
              <div className="row-between">
                <span className="muted">account</span>
                <span className="num" data-testid="sub-account-id">{conn.subAccountIdMasked || "not set"}</span>
              </div>
              <div className="row-between">
                <span className="muted">since</span>
                <span className="t-small muted-3">{conn.connectedAt ? manilaDateTime(conn.connectedAt) : "\u2014"}</span>
              </div>
              <div className="row-between">
                <span className="muted">tools last checked</span>
                <span className="t-small muted-3" data-testid="tools-verified-at">{conn.toolsVerifiedAt ? manilaDateTime(conn.toolsVerifiedAt) : "not yet"}</span>
              </div>
            </div>

            {!conn.providerConfigured ? (
              <div className="offline-banner" style={{ marginTop: 14 }} data-testid="stub-note">
                running on a safe stand-in for now — a live BloomConnect key will switch this to the real service, no changes needed here.
              </div>
            ) : null}

            <div className="divider" style={{ margin: "16px 0" }} />

            {!conn.connected ? (
              <div className="stack" style={{ gap: 12 }}>
                <Field label="your BloomConnect account id" htmlFor="sub-input" hint="paste the account id your BloomConnect gave you">
                  <Input id="sub-input" data-testid="sub-account-input" value={subInput}
                    onChange={(e) => setSubInput(e.target.value)} placeholder="e.g. bc-acct-1234" />
                </Field>
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <Button variant="primary" data-testid="connect-link-btn" disabled={!subInput.trim()}
                    onClick={() => setConfirm("connect-link")}><Link2 size={16} strokeWidth={1.75} />link account</Button>
                  <Button data-testid="connect-new-btn" onClick={() => setConfirm("connect-new")}>create a new one for me</Button>
                </div>
              </div>
            ) : (
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                <Button data-testid="verify-tools-btn" loading={busy} onClick={verifyTools}>
                  <ShieldCheck size={16} strokeWidth={1.75} />check tools
                </Button>
                <Button variant="danger" data-testid="disconnect-btn" onClick={() => setConfirm("disconnect")}>
                  <Unplug size={16} strokeWidth={1.75} />switch off
                </Button>
              </div>
            )}
          </Card>

          {/* MCP config */}
          <Card data-testid="mcp-card">
            <div className="row" style={{ gap: 10, marginBottom: 8 }}>
              <span className="ph-icon" style={{ margin: 0 }}><RefreshCw size={18} strokeWidth={1.75} /></span>
              <h2 className="t-h2">connect an assistant</h2>
            </div>
            <p className="muted t-small" style={{ marginBottom: 12 }}>
              Use this in a tool like Claude or Cursor to let it work with your shop through BloomConnect.
              Swap in your own shop key — never share it.
            </p>
            <div className="tbl-wrap" style={{ padding: 12, position: "relative" }}>
              <Button size="sm" data-testid="copy-mcp-btn" onClick={copyMcp}
                style={{ position: "absolute", top: 8, right: 8 }}>
                {copied ? <><Check size={14} strokeWidth={2} />copied</> : <><Copy size={14} strokeWidth={1.75} />copy</>}
              </Button>
              <pre className="mono t-small" data-testid="mcp-config" style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
{JSON.stringify(conn.mcpConfig, null, 2)}
              </pre>
            </div>
          </Card>
        </div>
      )}

      <div className="stack" style={{ gap: 16, marginTop: 20 }}>
        <ShopAiKeys shopId={shopId} onToast={(m) => { setToast(m); setTimeout(() => setToast(""), 3000); }} />
        <LowStockCard shopId={shopId} onToast={(m) => { setToast(m); setTimeout(() => setToast(""), 3000); }} />
        <McpApprovals shopId={shopId} />
        <McpKeys shopId={shopId} />
      </div>

      <ConfirmAction
        open={confirm === "connect-link"}
        title="Link BloomConnect to this shop?"
        preview={[
          `Connect ${shopName || "your shop"} to BloomConnect account "${subInput.trim()}".`,
          "Orders and points will flow to your conversations.",
          "You can switch this off any time.",
        ]}
        confirmLabel="Yes, link it" busy={busy} error={error}
        onConfirm={() => runConnect(false)} onCancel={() => setConfirm(null)}
      />
      <ConfirmAction
        open={confirm === "connect-new"}
        title="Create a new BloomConnect account?"
        preview={[
          `Create a fresh BloomConnect account for ${shopName || "your shop"}.`,
          "We will connect it to this shop right away.",
        ]}
        confirmLabel="Yes, create it" busy={busy} error={error}
        onConfirm={() => runConnect(true)} onCancel={() => setConfirm(null)}
      />
      <ConfirmAction
        open={confirm === "disconnect"}
        title="Switch off BloomConnect for this shop?"
        preview={[
          "Your shop will stop sending order updates to BloomConnect.",
          "Nothing in BloomConnect is deleted — you can reconnect later.",
        ]}
        confirmLabel="Yes, switch off" cancelLabel="No, keep it on" busy={busy} error={error}
        onConfirm={runDisconnect} onCancel={() => setConfirm(null)}
      />

      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="connections-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
